import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './JobDetailNew.module.css'; // Reusing your existing styles!
import api from '../../api/api.js';
import showToast from "../../components/ShowToast";
import Loader2 from '../../components/loader2';
import PostcodeAutocomplete from "../../components/PostcodeAutocomplete/PostcodeAutocomplete";
import { Search, FileText, Camera, Plus, Sparkles, MapPin, DollarSign, Clock, CheckCircle, ArrowLeft, X, ListChecks } from 'lucide-react';

// --- Main Wizard Component ---
const JobCreationWizard = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();
    const [projectId, setProjectId] = useState(null);

    // Resolve projectId passed from ProjectDetail via navigation state
    useEffect(() => {
        const pid = location.state?.projectId;
        if (pid !== undefined) {
            const num = Number(pid);
            setProjectId(Number.isNaN(num) ? null : num);
        }
        // Debug logging for verification
        // eslint-disable-next-line no-console
        console.log('[JobCreationWizard] location.state:', location.state, 'resolved projectId:', pid);
    }, [location.state]);
    // State to hold data across all steps
    const [jobData, setJobData] = useState({
        searchTerm: '',
        aiSuggestions: [], // To store suggestions from the AI
        professions: [], // Final selected professions { professionId, name }
        title: '',
        description: '',
        postcode: '',
        state: '',
        timeline: '',
        budgetRange: '',
        customBudget: '',
        images: [], // { key, url, file }
    });

    // Persist postcode between steps as a safety against any UI resets
    const postcodeRef = useRef(jobData.postcode || '');
    useEffect(() => {
        if (typeof jobData.postcode === 'string' && jobData.postcode.trim()) {
            postcodeRef.current = jobData.postcode;
        }
    }, [jobData.postcode]);

    const updateJobData = (field, value) => {
        setJobData(prev => ({ ...prev, [field]: value }));
    };

    // This handler decides where to go from Step 1.
    const handleStep1Next = (isAdvancedSearch = false) => {
        if (isAdvancedSearch) {
            // If the advanced search was used, go to the confirmation screen.
            setStep(2);
        } else {
            // If the user selected tags manually, skip the confirmation and go straight to details.
            setStep(3);
        }
    };

    const handlePrevStep = (currentStep) => {
        if (currentStep === 3 && jobData.aiSuggestions.length > 0) {
            // If we're going back from Step 3 AND we got here via Advanced Search,
            // go back to the confirmation screen (Step 2).
            setStep(2);
        } else {
            // Otherwise, always go back to Step 1.
            setStep(1);
        }
    };
    

    const handleSubmit = async () => {
        // Final validation
        if (jobData.professions.length === 0) { showToast('error', 'Please select at least one profession.'); setStep(2); return; }
        if (!jobData.title.trim() || jobData.title.trim().length < 10) { showToast('error', 'Please enter a descriptive job title (min 10 characters).'); setStep(3); return; }
        // Resolve postcode robustly in case UI controls reset it after image selection
        const effectivePostcode = (jobData.postcode ?? '').toString().trim() || (postcodeRef.current ?? '').toString().trim();
        if (!effectivePostcode) { showToast('error', 'Please enter a postcode.'); setStep(3); return; }

        setIsLoading(true);
        try {
            const homeUserId = localStorage.getItem('loggedInHomeUserId');
            if (!homeUserId) throw new Error("Could not identify user. Please log in again.");

            // Debug projectId before submit
            // eslint-disable-next-line no-console
            console.log('[JobCreationWizard] handleSubmit projectId:', projectId);

            const payload = {
                userId: parseInt(homeUserId, 10),
                name: jobData.title,
                description: jobData.description,
                profession: jobData.professions.map(p => ({ ProfessionId: p.professionId, name: p.name })),
                postcode: effectivePostcode,
                mystate: jobData.state,
                budget: jobData.budgetRange,
                budgetAmount: jobData.customBudget ? parseFloat(jobData.customBudget) : null,
                deadline: jobData.timeline,
                siteVisit: "By appointment",
                isPublish: true,
                isNew: true,
                // Ensure the selected projectId is sent to the backend
                projectId: projectId ?? null,
                userType: 'HomeUser' // dummy value - user server logged in user

            };

            // eslint-disable-next-line no-console
            console.log('[JobCreationWizard] Sending payload to /api/Jobs/createjob:', payload);

            const response = await api.post('/api/Jobs/createjob', payload);
            const newJobId = response.data.job_id;

            if (newJobId && jobData.images.length > 0) {
                const formData = new FormData();
                jobData.images.forEach(img => formData.append('files', img.file));
                await api.post(`/api/Jobs/upload/${newJobId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            }

            showToast('success', 'Your job has been posted successfully!');
            // Prefer returning to the Project Detail where this wizard was launched from
            if (projectId) {
                // eslint-disable-next-line no-console
                console.log('[JobCreationWizard] Navigating back to project detail with new job:', { projectId, newJobId });
                navigate(`/project/${projectId}`, { state: { selectedJobId: newJobId } });
                // Alternatively, to use the URL jobId param path:
                // navigate(`/project/${projectId}/${newJobId}`);
            } else {
                navigate('/newhomejobs'); // Fallback if no project context
            }

        } catch (error) {
            showToast('error', `Failed to post job: ${error.response?.data?.message || error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return <Step1_Profession onUpdate={updateJobData} onNext={handleStep1Next} setIsLoading={setIsLoading} jobData={jobData} />;
            case 2:
                return <Step2_ConfirmProfessions onUpdate={updateJobData} onNext={() => setStep(3)} onBack={() => setStep(1)} jobData={jobData} />;
            case 3:
                return <Step3_JobDetails onUpdate={updateJobData} onNext={() => setStep(4)} onBack={() => handlePrevStep(3)} jobData={jobData} />;
            case 4:
                return <Step4_Logistics onUpdate={updateJobData} onNext={() => setStep(5)} onBack={() => setStep(3)} jobData={jobData} />;
            case 5:
                return <Step5_ImagesAndSubmit onUpdate={updateJobData} onBack={() => setStep(4)} onSubmit={handleSubmit} jobData={jobData} />;
            default:
                return <Step1_Profession onUpdate={updateJobData} onNext={handleStep1Next} setIsLoading={setIsLoading} jobData={jobData} />;
        }
    };

    return (
        <div className={`${styles.jobDetailNew_minHeightScreen} ${styles.jobDetailNew_bgGradientPage}`}>
            <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_mainContent}`}>
                {isLoading ? <Loader2 /> : renderStep()}
            </div>
        </div>
    );
};




// --- CORRECTED Step 1 Component with Hybrid Logic ---
const Step1_Profession = ({ onUpdate, onNext, setIsLoading, jobData }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showFallback, setShowFallback] = useState(false);
    const dropdownRef = useRef(null);

    // --- EFFECT HOOK WITH THE FIX ---
    useEffect(() => {
        // Do not clear professions on every keystroke.
        // We only clear results and the fallback state.
        setShowFallback(false); 
        setResults([]);

        if (searchTerm.length < 3) {
            return;
        }

        const handler = setTimeout(async () => {
            setIsSearching(true);
            try {
                const response = await api.get(`/api/Jobs/AISmartSearch?search=${encodeURIComponent(searchTerm)}`);
                const matchedProfessions = (response.data || []).map(p => ({ professionId: p.id, name: p.name }));

                if (matchedProfessions.length > 0) {
                    setResults(matchedProfessions);
                } else {
                    // If fast search returns nothing, prepare to show the fallback option.
                    setShowFallback(true);
                }
            } catch (error) {
                console.error("Failed to fetch smart search results:", error);
                setShowFallback(true); // Show fallback on error too.
            } finally {
                setIsSearching(false);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

        const handleSelect = (profession) => {
        // Ensure we're always working with an array
        const currentProfessions = Array.isArray(jobData.professions) ? jobData.professions : [];
        
        // Check for duplicates
        if (!currentProfessions.some(p => p.professionId === profession.professionId)) {
            // Create the new array
            const newProfessions = [...currentProfessions, profession];
            // Set the new array in state
            onUpdate('professions', newProfessions);
        }
        
        // Reset the search UI
        setSearchTerm('');
        setResults([]);
        setShowFallback(false);
    };

        const handleReasoningSearch = async () => {
        setIsLoading(true);
        setShowFallback(false);
        try {
            const response = await api.post('/api/Jobs/AIReasoningSearch', { searchTerm });
            if (response.data && response.data.length > 0) {
                const aiSuggestions = response.data.map(p => ({ professionId: p.id, name: p.name }));
                onUpdate('aiSuggestions', aiSuggestions);
                onUpdate('professions', aiSuggestions);
                // --- KEY CHANGE ---
                // Call the onNext handler with 'true' to signal this was an advanced search.
                onNext(true); 
            } else {
                showToast('info', "Our advanced search couldn't find a specific match. Please try rephrasing your request.");
            }
        } catch (error) {
            showToast('error', `Advanced AI search failed: ${error.response?.data?.message || 'Please try again.'}`);
        } finally {
            setIsLoading(false);
        }
    };


    const handleRemove = (professionId) => {
        onUpdate('professions', jobData.professions.filter(p => p.professionId !== professionId));
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardContent}>
                <h2 className={styles.jobDetailNew_cardTitle} style={{justifyContent: 'center', fontSize: '1.5rem', marginBottom: '1rem'}}>What do you need done?</h2>
                <p className={styles.jobDetailNew_textGray600} style={{textAlign: 'center', marginBottom: '2rem'}}>Describe your job in your own words. We'll find the right professionals for you.</p>
                <div className={styles.jobDetailNew_inputWithIconContainer} ref={dropdownRef}>
                    <Search className={styles.jobDetailNew_inputIcon} />
                    <input type="text" placeholder="e.g., 'install IXL exhaust fan'" className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    
                    {isSearching && <div className={styles.jobDetailNew_dropdownResults}><p style={{padding: '1rem', color: '#6b7280'}}>Searching...</p></div>}
                    
                    {results.length > 0 && !isSearching && (
                        <div className={styles.jobDetailNew_dropdownResults}>
                            <p style={{padding: '0.5rem 1rem', fontSize: '0.8rem', color: '#6b7280', fontWeight: 'bold'}}>Did you mean...?</p>
                            {results.map(prof => (
                                <li key={prof.professionId} className={styles.jobDetailNew_resultItem} onClick={() => handleSelect(prof)}><Plus size={14} style={{marginRight: '8px'}}/> {prof.name}</li>
                            ))}
                        </div>
                    )}
                </div>

                {showFallback && !isSearching && (
                    <div style={{textAlign: 'center', marginTop: '1.5rem', padding: '1rem', backgroundColor: '#eff6ff', borderRadius: '8px'}}>
                        <p className={`${styles.jobDetailNew_textGray700} ${styles.jobDetailNew_mb2}`}>**Can't find the right profession?**</p>
                        <p className={`${styles.jobDetailNew_textGray600} ${styles.jobDetailNew_mb4}`} style={{fontSize: '0.9rem'}}>Let our advanced AI analyze your request to find the best matches for you.</p>
                        <button onClick={handleReasoningSearch} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`} style={{marginTop: '0.75rem'}}>
                            <Sparkles size={16} style={{marginRight: '8px'}}/> Use Advanced AI Search
                        </button>
                    </div>
                )}

                <div className={styles.jobDetailNew_flexWrap} style={{marginTop: '1.5rem', minHeight: '40px'}}>
                    {/* Add a check to ensure jobData.professions is an array before mapping */}
                    {Array.isArray(jobData.professions) && jobData.professions.map(prof => (
                        <span key={prof.professionId} className={styles.jobDetailNew_badge}>
                            {prof.name}
                            <button onClick={() => handleRemove(prof.professionId)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost} ${styles.jobDetailNew_buttonSm}`}><X className={styles.jobDetailNew_buttonIcon} /></button>
                        </span>
                    ))}
                </div>

                <div style={{marginTop: '2rem', textAlign: 'center'}}>
                    {/* The main 'Next' button now calls onNext with 'false' */}
                    <button onClick={() => onNext(false)} disabled={jobData.professions.length === 0} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`} style={{padding: '10px 40px'}}>Next</button>
                </div>


            </div>
        </div>
    );
};


// --- CORRECTED Step 2 Component ---
const Step2_ConfirmProfessions = ({ onUpdate, onNext, onBack, jobData }) => {
    
    const handleCheckboxChange = (profession, isChecked) => {
        // The profession object from aiSuggestions already has { professionId, name }
        // because we mapped it in the fallback search.
        let currentSelections = [...jobData.professions];

        if (isChecked) {
            if (!currentSelections.some(p => p.professionId === profession.professionId)) {
                currentSelections.push(profession);
            }
        } else {
            currentSelections = currentSelections.filter(p => p.professionId !== profession.professionId);
        }
        onUpdate('professions', currentSelections);
    };

    return (
         <div className={styles.jobDetailNew_card}>
             <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                    <ListChecks className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textGreen500}`} />
                    Confirm the professionals to notify
                </h3>
             </div>
             <div className={styles.jobDetailNew_cardContent}>
                <p className={styles.jobDetailNew_textGray600} style={{marginBottom: '1.5rem'}}>
                    Based on your request, our AI suggests notifying the following professionals. You can adjust the selection.
                </p>
                
                 <div className={styles.jobDetailNew_cardContentSpaceY4}>
                    {/* --- FIX #2: Defensive Rendering --- */}
                    {Array.isArray(jobData.aiSuggestions) && jobData.aiSuggestions.length > 0 ? (
                        jobData.aiSuggestions.map(prof => (
                            <div key={prof.id} className={styles.jobDetailNew_checkboxContainer}>
                                <input 
                                    type="checkbox" 
                                    id={`prof-${prof.id}`}
                                    checked={Array.isArray(jobData.professions) && jobData.professions.some(p => p.professionId === prof.id)}
                                    onChange={(e) => handleCheckboxChange(prof, e.target.checked)}
                                    style={{width: '20px', height: '20px', cursor: 'pointer', marginRight: '10px'}}
                                />
                                <label htmlFor={`prof-${prof.id}`} style={{fontWeight: 500, cursor: 'pointer', fontSize: '1rem'}}>
                                    {prof.name}
                                </label>
                            </div>
                        ))
                    ) : (
                        <p className={styles.jobDetailNew_textGray500}>
                            The AI couldn't find a direct match. Please go back and try rephrasing your request.
                        </p>
                    )}
                </div>


                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '2rem'}}>
                    <button onClick={onBack} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}>
                        <ArrowLeft size={16} style={{marginRight: '8px'}}/> Back
                    </button>
                    <button onClick={onNext} disabled={jobData.professions.length === 0} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}>
                        Next
                    </button>
                </div>
             </div>
        </div>
    );
};


// --- NEW Step 3: Job Details ---
const Step3_JobDetails = ({ jobData, onUpdate, onNext, onBack }) => {
    const handlePostcodeSelect = (location) => {
        onUpdate('postcode', location.fullAddress);
        onUpdate('state', location.state);
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}><FileText className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textBlue600}`} />Describe your job</h3>
            </div>
            <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div>
                    <label htmlFor="title" className={styles.jobDetailNew_label}>Job Title</label>
                    <input id="title" type="text" placeholder="e.g., Urgent repair for kitchen sink pipe" value={jobData.title || jobData.searchTerm} onChange={e => onUpdate('title', e.target.value)} className={styles.jobDetailNew_input} />
                    <p style={{fontSize: '0.8rem', color: '#6b7280', marginTop: '4px'}}>A clear title gets better responses.</p>
                </div>
                <div>
                    <label htmlFor="description" className={styles.jobDetailNew_label}>Description (Optional)</label>
                    <textarea id="description" placeholder="The more detail, the better the quotes. Mention materials, dimensions, and desired outcome." value={jobData.description} onChange={e => onUpdate('description', e.target.value)} className={styles.jobDetailNew_textarea} rows={4}></textarea>
                </div>
                <div>
                    <label htmlFor="postcode" className={styles.jobDetailNew_label}>Where is the job located?</label>
                    <PostcodeAutocomplete value={jobData.postcode} onSuggestionSelect={handlePostcodeSelect} />
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '2rem'}}>
                    <button onClick={onBack} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}><ArrowLeft size={16} style={{marginRight: '8px'}}/> Back</button>
                    <button onClick={onNext} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}>Next</button>
                </div>
            </div>
        </div>
    );
};

// --- NEW Step 4: Logistics ---
const Step4_Logistics = ({ jobData, onUpdate, onNext, onBack }) => {
    const budgetRanges = ["$500 - $1000", "$1000 - $2500", "$2500 - $5000", "$5000 - $10000", "$10000+"];
    const timelineOptions = ["Urgently", "Within 1 week", "Within 2 weeks", "2 weeks or longer"];

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}><DollarSign className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textGreen500}`} />Budget & Timeline</h3>
            </div>
            <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div>
                    <label className={styles.jobDetailNew_label}>What is your estimated budget?</label>
                     <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                        <select value={jobData.budgetRange} onChange={e => onUpdate('budgetRange', e.target.value)} className={styles.jobDetailNew_selectTrigger}>
                            <option value="">Select a range</option>
                            {budgetRanges.map(range => <option key={range} value={range}>{range}</option>)}
                        </select>
                        <div className={styles.jobDetailNew_inputWithIconContainer}>
                            <DollarSign className={styles.jobDetailNew_inputIcon} />
                            <input type="number" placeholder="Or enter exact amount" value={jobData.customBudget} onChange={e => onUpdate('customBudget', e.target.value)} className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`} />
                        </div>
                    </div>
                </div>
                 <div>
                    <label className={styles.jobDetailNew_label}>How soon do you need it done?</label>
                    <div className={styles.jobDetailNew_timelineButtonGrid}>
                        {timelineOptions.map(option => (
                             <button key={option} type="button" className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_timelineButton} ${jobData.timeline === option ? styles.jobDetailNew_bgBlue500 : styles.jobDetailNew_buttonOutline}`} onClick={() => onUpdate('timeline', option)}>
                                <span className={styles.jobDetailNew_timelineButtonText}>{option}</span>
                             </button>
                        ))}
                    </div>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '2rem'}}>
                    <button onClick={onBack} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}><ArrowLeft size={16} style={{marginRight: '8px'}}/> Back</button>
                    <button onClick={onNext} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}>Next</button>
                </div>
            </div>
        </div>
    );
};

// --- NEW Step 5: Images & Submit ---
const Step5_ImagesAndSubmit = ({ jobData, onUpdate, onBack, onSubmit }) => {
    const handleImageUpload = (e) => {
        // Debug current location data before mutating image state
        // eslint-disable-next-line no-console
        console.log('[JobCreationWizard] handleImageUpload before update', { postcode: jobData.postcode, state: jobData.state });
        const files = Array.from(e.target.files).slice(0, 4 - jobData.images.length);
        const newImages = files.map(file => ({ key: `new-${Date.now()}-${Math.random()}`, url: URL.createObjectURL(file), file }));
        onUpdate('images', [...jobData.images, ...newImages]);
    };

    const removeImage = (key) => {
        onUpdate('images', jobData.images.filter(img => img.key !== key));
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}><Camera className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textAmber600}`} />Add Photos (Optional)</h3>
            </div>
            <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div>
                    <p className={styles.jobDetailNew_textGray600}>Photos help professionals provide more accurate quotes. You can upload up to 4 images.</p>
                    <div className={styles.jobDetailNew_uploadArea} style={{marginTop: '1rem'}}>
                        <label htmlFor="file-upload" className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`} style={{cursor: 'pointer'}}>
                            <Camera size={16} style={{marginRight: '8px'}}/> Browse Files
                            <input id="file-upload" type="file" multiple accept="image/*" className="hidden" onChange={handleImageUpload} disabled={jobData.images.length >= 4} />
                        </label>
                    </div>
                    {jobData.images.length > 0 && (
                        <div className={styles.jobDetailNew_uploadedImagesGrid} style={{marginTop: '1.5rem'}}>
                            {jobData.images.map(img => (
                                <div key={img.key} className={styles.jobDetailNew_uploadedImageItem}>
                                    <img src={img.url} alt="upload preview" className={styles.jobDetailNew_uploadedImage} />
                                    <button onClick={() => removeImage(img.key)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_imageRemoveBtn}`}><X className={styles.jobDetailNew_buttonIcon} /></button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '2rem'}}>
                    <button onClick={onBack} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}><ArrowLeft size={16} style={{marginRight: '8px'}}/> Back</button>
                    <button onClick={onSubmit} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}><CheckCircle size={16} style={{marginRight: '8px'}}/> Post My Job</button>
                </div>
            </div>
        </div>
    );
};

export default JobCreationWizard;
